import { atom, useAtom, useSetAtom } from 'jotai';
import * as _ from 'lodash-es';
import type { Op } from 'quill/core';
import { useMemo, useState } from 'react';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import { ANNOTATION_CREATION_ID } from '../PendingMagneticWallItemProvider';

const ANNOTATION_FRAGMENT = gql(`
  fragment useAnnotationState_annotation on Annotation {
    id
    content
    link
    linkText
  }
`);

export type AnnotationState = {
  content: Op[];
  linkText: string;
  link: string;
};

const annotationsStateAtom = atom<Record<string, AnnotationState>>({});

export const useClearAnnotationsState = () => {
  const setAnnotationsState = useSetAtom(annotationsStateAtom);
  return () => setAnnotationsState({});
};

const useAnnotationState = (
  annotation: DocumentType<typeof ANNOTATION_FRAGMENT>,
) => {
  const isNewAnnotation = annotation.id === ANNOTATION_CREATION_ID;
  const [isEditing, setIsEditing] = useState(isNewAnnotation);
  const [annotationsState, setAnnotationsState] = useAtom(annotationsStateAtom);
  const initialAnnotationState = useMemo(
    () => ({
      content: annotation.content,
      linkText: annotation.linkText ?? '',
      link: annotation.link ?? '',
    }),
    [annotation.content, annotation.link, annotation.linkText],
  );

  const annotationState = isEditing
    ? (annotationsState[annotation.id] ?? initialAnnotationState)
    : initialAnnotationState;

  const updateAnnotationState = (value: Partial<AnnotationState>) => {
    setAnnotationsState((current) => {
      const existing = current[annotation.id];

      return {
        ...current,
        [annotation.id]: { ...initialAnnotationState, ...existing, ...value },
      };
    });
  };

  const clearAnnotationState = () =>
    setAnnotationsState((current) => _.omit(current, annotation.id));

  const cancelEditing = () => {
    setIsEditing(false);
    if (!isNewAnnotation) {
      clearAnnotationState();
    }
  };

  const finishEditing = () => {
    setIsEditing(false);
    clearAnnotationState();
  };

  const startEditing = () => setIsEditing(true);

  return {
    annotationState,
    updateAnnotationState,
    isEditing,
    cancelEditing,
    startEditing,
    finishEditing,
  };
};

export default useAnnotationState;
